import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, useMatch } from 'react-router-dom';

import ListingService from './services/ListingService';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import PopularAreas from './components/PopularAreas';
import PropertyListings from './components/PropertyListings';
import NewProjectLaunch from './components/NewProjectLaunch';
import ServicesBar from './components/ServicesBar';
import Footer from './components/Footer';
import MobileBottomNav from './components/MobileBottomNav';
import AreaPropertyListing from './components/AreaPropertyListing';
import AllAreasPage from './components/AllAreasPage';
import AllListingsPage from './components/AllListingsPage';
import PropertyDetailModal from './components/PropertyDetailModal';
import PrivacyPolicyModal from './components/PrivacyPolicyModal';
import GuidesPage from './components/GuidesPage';
import GuideDetailPage from './components/GuideDetailPage';
import ExclusiveListingsPage from './components/ExclusiveListingsPage';
import OAuthCallbackPage from './components/OAuthCallbackPage';

import './css/styles.css';

function App() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isPrivacyRoute = location.pathname === '/privacy-policy';

  const handleOpenPrivacy = () => {
    if (location.pathname !== '/privacy-policy') {
      navigate('/privacy-policy', { state: { backgroundLocation: location } });
    }
  };

  const handleClosePrivacy = () => {
    setShowPrivacyModal(false);
    if (location.pathname === '/privacy-policy') {
      if (location.state && location.state.backgroundLocation) {
        navigate(-1);
      } else {
        navigate('/');
      }
    }
  };

  const propertyRouteMatch = useMatch('/property/:propertyId/*');
  const propertyIdFromUrl = propertyRouteMatch?.params?.propertyId;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await ListingService.getAllData();
        setData(res);
      } catch (err) {
        console.error("Failed to load listing data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Dynamic SEO meta updates for homepage, privacy policy, and route changes
  useEffect(() => {
    if (location.pathname === '/' && !propertyIdFromUrl) {
      document.title = "Nicole Pua Property Agent (妮可地产经纪) | Top Johor Bahru & Iskandar Puteri Real Estate Specialist";
      
      let metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', "Nicole Pua (妮可地产经纪) - Trusted Johor Bahru (JB) Property Agent & Iskandar Puteri Real Estate Specialist for Bukit Indah, Horizon Hills, Eco Botanic, Wawari, Danga Bay, R&F, Mutiara Rini, and Medini. Buy, sell & rent properties. Contact +6016-3288120 for expert guidance.");
      }

      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (canonicalLink) {
        canonicalLink.setAttribute('href', "https://www.nicolejbproperty.com/");
      }
    } else if (location.pathname === '/privacy-policy') {
      document.title = "Privacy Policy | Nicole Pua Property Agent";

      let metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', "Privacy Policy for Nicole Pua Real Estate Negotiator. Information on how we collect, use, and protect your personal data under Malaysia PDPA 2010.");
      }

      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (canonicalLink) {
        canonicalLink.setAttribute('href', "https://www.nicolejbproperty.com/privacy-policy");
      }
    }
  }, [location.pathname, propertyIdFromUrl]);

  // Sync URL route with selected property modal for SEO and direct URL access
  useEffect(() => {
    if (propertyIdFromUrl) {
      if (selectedProperty && (String(selectedProperty.id) === String(propertyIdFromUrl) || String(selectedProperty.linkUrl) === String(propertyIdFromUrl))) {
        return;
      }

      // Check pre-loaded data first
      if (data && data.listings) {
        const found = data.listings.find(p => String(p.id) === String(propertyIdFromUrl) || String(p.linkUrl) === String(propertyIdFromUrl));
        if (found) {
          setSelectedProperty(found);
          return;
        }
      }

      // Fetch property directly from service if accessed via URL directly
      ListingService.getPropertyById(propertyIdFromUrl).then(item => {
        if (item) {
          setSelectedProperty(item);
        }
      });
    } else {
      if (selectedProperty) {
        setSelectedProperty(null);
      }
    }
  }, [propertyIdFromUrl, data]);

  const backgroundLocation = location.state && location.state.backgroundLocation;

  const handleSelectProperty = (item) => {
    if (!item) return;
    setSelectedProperty(item);
    const linkSlug = item.linkUrl || (item.title ? item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') + '-' + item.id : item.id);
    const targetPath = `/property/${linkSlug}`;
    
    if (location.pathname !== targetPath) {
      navigate(targetPath, { state: { backgroundLocation: location } });
    }
  };

  const handleClosePropertyModal = () => {
    setSelectedProperty(null);
    if (location.pathname.startsWith('/property/')) {
      if (location.state && location.state.backgroundLocation) {
        navigate(-1);
      } else {
        navigate('/all-listings');
      }
    }
  };

  const handleSelectArea = (area) => {
    const slug = area.title.toLowerCase().replace(/\s+/g, '-');
    navigate(`/area/${slug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div style={{
        background: '#090d16',
        color: '#d4af37',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.5rem',
        fontFamily: 'Montserrat, sans-serif'
      }}>
        <i className="bi bi-arrow-repeat spin" style={{ marginRight: '0.5rem' }}></i> Loading Nicole Pua Property Agent...
      </div>
    );
  }

  const MainHomeContent = (
    <>
      <HeroSection agent={data?.agent} />
      <PopularAreas areas={data?.areas} listings={data?.listings} onSelectArea={handleSelectArea} />
      
      <section className="section-wrapper" id="properties">
        <div className="middle-section-grid">
          <PropertyListings 
            listings={data?.listings} 
            agent={data?.agent} 
            onSelectProperty={handleSelectProperty}
          />
          <NewProjectLaunch newLaunch={data?.newLaunch} agent={data?.agent} />
        </div>
      </section>
    </>
  );

  return (
    <div className="app-main-wrapper">
      <Navbar agent={data?.agent} onGoHome={handleGoHome} />

      <Routes location={backgroundLocation || location}>
        <Route path="/all-areas" element={
          <AllAreasPage 
            areas={data?.areas}
            listings={data?.listings}
            agent={data?.agent}
            onGoBack={handleGoHome}
          />
        } />
        <Route path="/areas" element={
          <AllAreasPage 
            areas={data?.areas}
            listings={data?.listings}
            agent={data?.agent}
            onGoBack={handleGoHome}
          />
        } />
        <Route path="/all-listings" element={
          <AllListingsPage 
            areas={data?.areas}
            agent={data?.agent}
            onGoBack={handleGoHome}
            onSelectProperty={handleSelectProperty}
          />
        } />
        <Route path="/listings" element={
          <AllListingsPage 
            areas={data?.areas}
            agent={data?.agent}
            onGoBack={handleGoHome}
            onSelectProperty={handleSelectProperty}
          />
        } />
        <Route path="/area/:areaSlug" element={
          <AreaPropertyListing 
            areas={data?.areas}
            agent={data?.agent} 
            onGoBack={handleGoHome} 
            onSelectProperty={handleSelectProperty}
          />
        } />
        <Route path="/guides" element={
          <GuidesPage 
            agent={data?.agent}
            onGoBack={handleGoHome}
          />
        } />
        <Route path="/guides/:articleSlug" element={
          <GuideDetailPage 
            agent={data?.agent}
          />
        } />
        <Route path="/exclusive/:linkKey" element={
          <ExclusiveListingsPage 
            agent={data?.agent}
            onSelectProperty={handleSelectProperty}
          />
        } />
        <Route path="/oauth2callback" element={<OAuthCallbackPage />} />
        <Route path="/privacy-policy" element={MainHomeContent} />
        <Route path="/property/:propertyId/*" element={MainHomeContent} />
        <Route path="*" element={MainHomeContent} />
      </Routes>

      <ServicesBar />
      <Footer testimonials={data?.testimonials} onOpenPrivacy={handleOpenPrivacy} />
      <MobileBottomNav onGoHome={handleGoHome} />

      {selectedProperty && (
        <PropertyDetailModal 
          property={selectedProperty} 
          agent={data?.agent} 
          onClose={handleClosePropertyModal} 
        />
      )}

      {(showPrivacyModal || isPrivacyRoute) && (
        <PrivacyPolicyModal onClose={handleClosePrivacy} />
      )}
    </div>
  );
}

export default App;
